/**
 * GetStarted — Role Selection Page
 * Route: /get-started
 *
 * The conversion point. User picks their role → goes to signup with role pre-selected.
 * Design: Clean, dark, decisive. One question. Four paths.
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Building2, BarChart3, GitBranch, Landmark, ArrowRight, ChevronRight
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const ROLES = [
  {
    id:      "sme",
    label:   "SME",
    title:   "I run a business",
    desc:    "Measure emissions, build an action plan, access financing for green improvements.",
    icon:    Building2,
    color:   "#059669",
    bullets: ["Scope 1 & 2 tracking", "Action plan with CAPEX", "Finance application"],
    cta:     "Start SME Journey",
  },
  {
    id:      "corporate",
    label:   "Corporate",
    title:   "I manage corporate emissions",
    desc:    "Group-wide emissions tracking, disclosure preparation, and climate project pipeline.",
    icon:    BarChart3,
    color:   "#0891B2",
    bullets: ["IFRS S2 / TCFD / CDP", "Multi-site management", "Supply chain tracking"],
    cta:     "Start Corporate Journey",
  },
  {
    id:      "project",
    label:   "Project Developer",
    title:   "I develop climate projects",
    desc:    "Structure climate assets, manage governance, and prepare for carbon market participation.",
    icon:    GitBranch,
    color:   "#7C3AED",
    bullets: ["Baseline & MRV governance", "LoA and Article 6 support", "Asset structuring"],
    cta:     "Start Project Journey",
  },
  {
    id:      "fi",
    label:   "Financial Institution",
    title:   "I evaluate and finance projects",
    desc:    "Review structured financing applications, assess deal quality, and manage your pipeline.",
    icon:    Landmark,
    color:   "#D97706",
    bullets: ["Incoming finance queue", "Data-backed deal review", "Portfolio risk view"],
    cta:     "Start FI Journey",
  },
];

export default function GetStarted() {
  const navigate      = useNavigate();
  const [params]      = useSearchParams();
  const preselected   = params.get("role");
  const [selected, setSelected] = useState(preselected || null);
  const [hovering, setHovering] = useState(null);

  const active = selected || hovering;
  const activeRole = ROLES.find(r => r.id === active);

  const handleContinue = () => {
    if (!selected) return;
    navigate(`/signup?role=${selected}`);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col" data-testid="get-started-page">
      {/* Top bar */}
      <div className="px-6 h-16 flex items-center justify-between border-b border-white/8">
        <Link to="/">
          <img src={LOGO_URL} alt="DeCarbonIQ" className="h-6 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        </Link>
        <Link to="/login" className="text-xs text-slate-500 hover:text-white transition-colors">
          Already have an account? Sign in →
        </Link>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-4xl">
          {/* Headline */}
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-3">
              What best describes you?
            </h1>
            <p className="text-slate-400 text-base">
              We'll personalise your experience based on your role.
            </p>
          </div>

          {/* Role grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {ROLES.map(role => {
              const Icon   = role.icon;
              const isSelected = selected === role.id;
              const isHover    = hovering === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => setSelected(role.id)}
                  onMouseEnter={() => setHovering(role.id)}
                  onMouseLeave={() => setHovering(null)}
                  className={`relative text-left rounded-xl p-5 border-2 transition-all group ${
                    isSelected
                      ? "border-[#059669] bg-[#059669]/8"
                      : "border-white/10 bg-white/4 hover:border-white/20 hover:bg-white/8"
                  }`}
                  data-testid={`role-select-${role.id}`}>

                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#059669] flex items-center justify-center">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}

                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${role.color}20` }}>
                    <Icon size={18} style={{ color: role.color }} />
                  </div>

                  {/* Text */}
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: role.color }}>
                    {role.label}
                  </p>
                  <p className="text-sm font-bold text-white mb-2 leading-snug">{role.title}</p>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">{role.desc}</p>

                  {/* Bullets */}
                  <ul className="space-y-1">
                    {role.bullets.map(b => (
                      <li key={b} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        <span style={{ color: role.color }}>—</span> {b}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          {/* Continue button */}
          <div className="flex flex-col items-center gap-4">
            <button
              onClick={handleContinue}
              disabled={!selected}
              className={`flex items-center gap-3 px-8 py-4 font-bold rounded-xl text-base transition-all ${
                selected
                  ? "bg-[#059669] text-white hover:bg-[#047857] shadow-lg shadow-[#059669]/25"
                  : "bg-white/5 text-slate-500 cursor-not-allowed"
              }`}
              data-testid="get-started-continue-btn">
              {selected && activeRole ? activeRole.cta : "Select a role to continue"}
              {selected && <ArrowRight size={18} />}
            </button>
            {!selected && (
              <p className="text-xs text-slate-600">Select one of the four options above</p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom disclaimer */}
      <div className="px-6 py-4 border-t border-white/5 text-center">
        <p className="text-[10px] text-slate-700">
          Free to get started. No credit card required.
          All workspace access is role-controlled and secure.
        </p>
      </div>
    </div>
  );
}
